import { App, URLOpenListenerEvent } from "@capacitor/app";
import BRANDS from "@elit-hair/brands";
import {
    CheckPermissionsModes,
    ElitAuthProvider,
    checkPermissions,
    getAuthorizationErrorMessage,
    useAuth,
} from "@elit-hair/elit-tools";
import { Button, THEMES, useTheme } from "@elit-hair/elit-ui";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingScreen } from "@/components/loading-screen";
import { ENV } from "@/constants/env";
import { useCapacitor } from "@/hooks/use-capacitor";
import { setAuthTokensUpdateListener } from "@/utils/auth-token.utils";

const UnauthorizedScreen = ({ permissions }: { permissions?: string[] }) => {
    const { t } = useTranslation();
    const { userData } = useAuth();
    const navigate = useNavigate();

    // Generate dynamic error message based on missing permissions
    const errorMessage =
        permissions && userData?.permissionCodes
            ? getAuthorizationErrorMessage(userData.permissionCodes, permissions, "view this page")
            : t("sadly_you_are_not_authorized_to_view_this_page");

    return (
        <div className="flex flex-col justify-center items-center w-full h-screen bg-elitDarkBlue">
            <h1 className="text-2xl font-bold">{t("unauthorized_page")}</h1>
            <p className="text-lg mb-4 text-center max-w-md">{errorMessage}</p>
            <Button
                onClick={() => {
                    navigate({ to: "/", replace: true });
                }}
            >
                {t("back_to_home")}
            </Button>
        </div>
    );
};

// For authentication in mobile, check useCapacitor hook
// There is the event triggered by cordova when the app is ready

export function ProtectedRoute({ children, permissions }: { children: React.ReactNode; permissions?: string[] }) {
    const { i18n } = useTranslation();
    const { auth, setAuth, userData, handleLogout } = useAuth();

    const { isCordova } = useCapacitor();
    const { theme: currentTheme } = useTheme();
    const [authCode, setAuthCode] = useState<string | undefined>(undefined);

    useEffect(() => {
        const handleUrlOpen = (event: URLOpenListenerEvent) => {
            const url = event.url;
            const urlParams = new URLSearchParams(url.split("?")[1]);
            const code = urlParams.get("code");
            if (code) {
                setAuthCode(code);
            }
        };

        App.addListener("appUrlOpen", handleUrlOpen);

        return () => {
            App.removeAllListeners();
        };
    }, [setAuthCode]);

    useEffect(() => {
        setAuthTokensUpdateListener(({ accessToken, refreshToken }) => {
            setAuth({ accessToken, refreshToken });
        });
        return () => setAuthTokensUpdateListener(null);
    }, [setAuth]);

    useEffect(() => {
        const codes = userData?.permissionCodes;
        if (Array.isArray(codes) && codes.length === 0) {
            handleLogout();
        }
    }, [userData, handleLogout]);

    const handleAuthenticated = useCallback(
        (accessToken: string, refreshToken: string) => {
            setAuth({ accessToken, refreshToken });
            setAuthCode(undefined);
        },
        [setAuth]
    );

    const authorized = useMemo(() => {
        return checkPermissions(userData?.permissionCodes, permissions, CheckPermissionsModes.ANY);
    }, [userData?.permissionCodes, permissions]);

    const authServices = useMemo(
        () => [
            {
                img: BRANDS.elithair.logoSquare || "",
                color: "#eee",
                url: ENV.ZOHO_ELITHAIR_SSO_URL,
            },
            {
                img: BRANDS.rhinomedica.logoSquare || "",
                color: "#eee",
                url: ENV.ZOHO_RHINOMEDICA_SSO_URL,
            },
            {
                img: BRANDS.yessmile.logoSquare || "",
                color: "#eee",
                url: ENV.ZOHO_YESSMILE_SSO_URL,
            },
        ],
        []
    );

    const theme = useMemo(() => (currentTheme === THEMES.DARK ? "dark" : "light"), [currentTheme]);

    return (
        <ElitAuthProvider
            authenticated={!!auth}
            authorized={authorized}
            customValidationUrl={`${ENV.AUTH_SVC_URL}/auth/sso/login`}
            customRedirectUrl={isCordova ? ENV.APP_URL : undefined}
            loginUrl={ENV.CUSTOM_LOGIN_URL}
            provider="elithair"
            exchangeCodeUrl={`${ENV.AUTH_SVC_URL}/auth/sso/exchange-code`}
            style="D"
            appName="Elitphone"
            lang={i18n.language}
            onAuthenticated={handleAuthenticated}
            redirectingScreen={<LoadingScreen />}
            authenticatingScreen={<LoadingScreen />}
            unauthorizedScreen={<UnauthorizedScreen permissions={permissions} />}
            customAuthCode={authCode}
            authServices={authServices}
            theme={theme}
        >
            {children}
        </ElitAuthProvider>
    );
}
